import React, { lazy, Suspense, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import LoadingPage from '../../../pages/LoadingPage'
import { Typography } from '@mui/material'
import { notifyError } from '../../../utils/notifyError'
import { EmailManager } from '../../../APIManager'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import PAGE_TITLES from '../../../consts/titles'
import { formatDateByUserTimeZone } from '../../../helpers/formatDateByUserTimeZone'
import { type IEmailDeliveryEventListResponse } from '../../../models/EmailDeliveryEvent'

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

export const EmailHistoryTab = (): JSX.Element => {
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({
    search: '',
    pageSize: 10,
    pageNumber: 1
  })

  const { data: emailHistoryList, isLoading, isError: isEmailHistoryError, error: emailHistoryError } = useQuery({
    queryKey: ['emailDeliveryEvents', params],
    queryFn: async (): Promise<IEmailDeliveryEventListResponse> => {
      const result = await EmailManager.getEmailDeliveryEvents({
        search: params.search || undefined,
        sort: typeof params.sort === 'string' ? params.sort : undefined,
        pageSize: params.pageSize,
        pageNumber: params.pageNumber
      })
      return result.data
    }
  })

  useEffect(() => {
    if (isEmailHistoryError) notifyError(emailHistoryError, { fallbackMessage: 'Failed to load email history. Please try again.' })
  }, [isEmailHistoryError])

  const tableHeader = emailHistoryList?.header?.headerName ?? PAGE_TITLES.EMAIL
  const overrides = {
    sentAt: ({ sentAt }: { sentAt?: string | null }) => (
      <Typography>
        {sentAt != null && sentAt !== '' ? formatDateByUserTimeZone(sentAt, 'MM/dd/yyyy') : ''}
      </Typography>
    )
  }

  return (
    <Suspense fallback={<LoadingPage />}>
      <AppTable
        topbarTitle={tableHeader}
        list={emailHistoryList}
        rows={emailHistoryList?.data ?? []}
        columns={emailHistoryList?.keys ?? []}
        loading={isLoading}
        overrides={overrides}
        featureName='Email History'
        hideEditColumn={true}
        hideActionsColumn={true}
        disableRowDoubleClick={true}
        params={params}
        setParams={setParams}
      />
    </Suspense>
  )
}
